import { Icon } from '@iconify/react';
import { ReactNode, useMemo } from 'react';

import CashIcon from 'components/icons/CashIcon';
import CentralDataHubIcon from 'components/icons/CentralDataHubIcon';
import PeriodCloseIcon from 'components/icons/PeriodCloseIcon';
import ReportingIcon from 'components/icons/ReportingIcon';
import RevenueIcon from 'components/icons/RevenueIcon';
import SettingsIcon from 'components/icons/SettingsIcon';

import { useCustomerFeatureFlags } from 'hooks/useCustomerFlags';
import { useIsMaximorUser } from 'hooks/useIsMaximorUser';

import { ALLOW_DEVELOPMENT, featureFlags } from 'utils/config/featureFlags';

import type { Module, ModuleName } from 'types/auth/Module';

const {
  FF__DISPLAY_ALL_PAGES,
  FF__SHOW_WORKFLOWS_MODULE,
  FF__SHOW_AP_AND_ACCRUALS,
  FF__SHOW_FIXED_ASSETS,
  FF__SHOW_PREPAIDS,
  FF__SHOW_REVENUE_OVERVIEW,
  FF__SHOW_REVENUE_RECONCILIATION_TAB,
  FF__SHOW_REVENUE_REPORTING_TAB,
  FF__CASH_DASHBOARD,
  FF__CASH_REPORTING,
  FF__CASH_POLICIES,
  FF__SHOW_CURRENCY_CONVERSION,
  FF__DATA_CATALOG_DEMO,
  FF__CLOSE_DEMO_PAGES,
} = featureFlags;

const defaultIconProps = {
  className: 'mr-2 w-4 h-4',
};

type FEOnlyModuleName =
  | 'assets'
  | 'ap-and-accruals'
  | 'fixed-assets'
  | 'prepaid'
  | 'period-close'
  | 'maximor-reporting'
  | 'central-data-hub'
  | 'data-catalog'
  | 'workflows';

type MenuItemBase = {
  icon: ReactNode;
  /** Permission key for RBAC check + AntdMenu item key */
  permissionKey: ModuleName | `${ModuleName}-${string}` | FEOnlyModuleName;
  label: string;
  /** true: always-show | false: always-hide | undefined: use RBAC */
  showdemoitem?: boolean;
};

export type MenuItemChild = Omit<MenuItemBase, 'icon'> & {
  path: string;
  /** When true, item is only visible to Maximor users on target customer workspaces */
  maximorInternalOnly?: boolean;
};

type MenuItemWithPath = MenuItemBase & {
  path: string;
  children?: never;
};

type MenuItemWithChildren = MenuItemBase & {
  children: MenuItemChild[];
  path?: never;
};

export type MenuItem = MenuItemWithPath | MenuItemWithChildren;

export const menuItems: MenuItem[] = [
  {
    permissionKey: 'revenue',
    icon: <RevenueIcon {...defaultIconProps} />,
    label: 'Revenue',
    children: [
      {
        permissionKey: 'revenue-overview',
        label: 'Overview',
        path: 'revenue/overview',
        showdemoitem: FF__SHOW_REVENUE_OVERVIEW,
      },
      {
        permissionKey: 'revenue-recognition',
        label: 'Recognition',
        path: 'revenue/recognition/deals',
      },
      {
        permissionKey: 'revenue-reconciliation',
        label: 'Reconciliation',
        path: 'revenue/reconciliation/invoice-matching',
        showdemoitem: FF__SHOW_REVENUE_RECONCILIATION_TAB,
      },
      {
        permissionKey: 'revenue-reporting',
        label: 'Reporting',
        path: 'revenue/reporting',
        showdemoitem: FF__SHOW_REVENUE_REPORTING_TAB,
      },
      {
        permissionKey: 'revenue-policies',
        label: 'Policies',
        path: 'revenue/policies/contract-items',
      },
    ],
  },
  {
    permissionKey: 'cash',
    icon: <CashIcon {...defaultIconProps} />,
    label: 'Cash',
    children: [
      {
        permissionKey: 'cash-dashboard',
        label: 'Overview',
        path: 'cash/overview',
        showdemoitem: FF__CASH_DASHBOARD,
      },
      {
        permissionKey: 'cash-accounting-and-reconciliation',
        label: 'Accounting & Reconciliation',
        path: 'cash/accounting-and-reconciliation',
      },
      {
        permissionKey: 'cash-reporting',
        label: 'Reporting',
        path: 'cash/reporting',
        showdemoitem: FF__CASH_REPORTING,
      },
      {
        permissionKey: 'cash-policies',
        label: 'Policies',
        path: 'cash/policies',
        showdemoitem: FF__CASH_POLICIES,
      },
    ],
  },
  {
    permissionKey: 'assets',
    icon: <Icon icon="ant-design:bar-chart-outlined" {...defaultIconProps} />,
    label: 'Subledgers',
    children: [
      {
        permissionKey: 'ap-and-accruals',
        label: 'AP & Accruals',
        path: 'ap-and-accruals',
        showdemoitem: FF__SHOW_AP_AND_ACCRUALS,
      },
      {
        permissionKey: 'fixed-assets',
        label: 'Fixed Assets',
        path: 'fixed-assets',
        showdemoitem: FF__SHOW_FIXED_ASSETS,
      },
      {
        permissionKey: 'prepaid',
        label: 'Prepaids',
        path: 'prepaid',
        showdemoitem: FF__SHOW_PREPAIDS,
      },
    ],
  },
  {
    icon: <PeriodCloseIcon {...defaultIconProps} />,
    permissionKey: 'period-close',
    label: 'Period Close',
    children: [
      {
        permissionKey: 'close-overview',
        label: 'Insights', // TODO: Rename to "Overview" this once we have the timeline or month close sections
        path: 'close/overview',
      },
      {
        permissionKey: 'checklist',
        label: 'Checklist',
        path: 'close/checklist',
      },
      {
        permissionKey: 'checklist-v1',
        label: 'Checklist (V1)',
        path: 'close/checklist-v1',
        maximorInternalOnly: true,
      },
      {
        permissionKey: 'checklist-journal-entries',
        label: 'Journal Entries',
        path: 'close/journal-entries',
        showdemoitem: FF__CLOSE_DEMO_PAGES,
      },
      {
        permissionKey: 'reconciliations',
        label: 'Reconciliations',
        path: 'close/reconciliations',
      },
      {
        permissionKey: 'reconciliations-v1',
        label: 'Reconciliations (V1)',
        path: 'close/reconciliations-v1',
        maximorInternalOnly: true,
      },
      {
        permissionKey: 'flux-analysis',
        label: 'Flux Analysis',
        path: 'flux-analysis',
      },
      {
        permissionKey: 'monitoring',
        label: 'Monitoring',
        path: 'monitoring',
      },
    ],
  },
  {
    permissionKey: 'maximor-reporting',
    icon: <ReportingIcon {...defaultIconProps} />,
    label: 'Reporting',
    children: [
      {
        permissionKey: 'reports-hub',
        label: 'Reports Hub',
        path: 'reporting/reports-hub',
      },
      {
        permissionKey: 'consolidation-mapping',
        label: 'Consolidation Mapping',
        path: 'reporting/consolidation-mapping',
      },
      {
        permissionKey: 'intercompany-eliminations',
        label: 'Intercompany Eliminations',
        path: 'reporting/intercompany-eliminations',
      },
    ],
  },
  {
    permissionKey: 'central-data-hub',
    icon: <CentralDataHubIcon {...defaultIconProps} />,
    label: 'Central Data Hub',
    children: [
      {
        permissionKey: 'unified-ledger',
        label: 'Unified Ledger',
        path: 'central-data-hub/unified-ledger',
      },
      {
        permissionKey: 'data-catalog',
        label: 'Data Catalog',
        path: 'central-data-hub/data-catalog',
        showdemoitem: FF__DATA_CATALOG_DEMO,
      },
      {
        permissionKey: 'mapping',
        label: 'Mapping',
        path: 'central-data-hub/mapping',
      },
      {
        permissionKey: 'integrations',
        label: 'Integrations',
        path: 'central-data-hub/integrations',
      },
    ],
  },
  {
    permissionKey: 'workflows',
    icon: <Icon icon="lucide:workflow" {...defaultIconProps} />,
    label: 'Workflows',
    path: 'workflows',
    showdemoitem: FF__SHOW_WORKFLOWS_MODULE,
  },
  {
    permissionKey: 'organization-settings',
    icon: <SettingsIcon {...defaultIconProps} />,
    label: 'Settings',
    children: [
      {
        permissionKey: 'organization-settings-authentication',
        label: 'Authentication',
        path: 'organization-settings/authentication',
      },
      {
        permissionKey: 'organization-settings-exchange-rates',
        label: 'Exchange Rates',
        path: 'organization-settings/exchange-rates',
        showdemoitem: FF__SHOW_CURRENCY_CONVERSION,
      },
    ],
  },
];

export const keyPathToMenuObj = (
  keyPath: string[]
): MenuItem | MenuItemChild | null => {
  if (keyPath.length === 0) return null;
  if (menuItems.length === 0) return null;
  const item = menuItems.find((i) => i.permissionKey === keyPath[0]);
  if (!item) return null;
  if (!keyPath[1]) {
    return item;
  }
  return item.children?.find((c) => c.permissionKey === keyPath[1]) || null;
};

export function useMenu(
  userAccessibleModules: Module[] = [],
  modulesLoaded: boolean = true
) {
  const isMaximorUser = useIsMaximorUser();
  const customerFlags = useCustomerFeatureFlags();
  const isTargetCustomer =
    customerFlags.FF__IS_FACILIO_CUSTOMER ||
    customerFlags.FF__IS_RENTLY_CUSTOMER ||
    customerFlags.FF__IS_INVST_CUSTOMER;
  const showMaximorInternalItems = isMaximorUser && isTargetCustomer;

  // Memoize filtered menu items to prevent recalculation on every render
  const { links, hasPathPermission, hasModulePermission } = useMemo(() => {
    // Security: Return empty menu while modules are loading to prevent showing stale data.
    // In development, we always allow the menu to render to simplify local testing and demos.
    if (!modulesLoaded && !ALLOW_DEVELOPMENT) {
      return {
        links: [],
        hasPathPermission: () => false,
        hasModulePermission: () => false,
      };
    }

    const hasModulePermission = (
      permissionKey: string,
      moduleName: Module['name']
    ): boolean => {
      return (
        moduleName === 'All' ||
        moduleName === permissionKey ||
        permissionKey.startsWith(`${moduleName}-`)
      );
    };

    // Check if a single item (or child) has permission
    const hasItemPermission = (
      item: MenuItemWithPath | MenuItemChild
    ): boolean => {
      // Maximor-internal items: only visible for Maximor users on target customers
      if ('maximorInternalOnly' in item && item.maximorInternalOnly) {
        return showMaximorInternalItems;
      }

      // FF__DISPLAY_ALL_PAGES is highest priority - show everything
      if (FF__DISPLAY_ALL_PAGES) return true;

      // Demo flag takes precedence over module permissions
      if (item.showdemoitem !== undefined) return item.showdemoitem;

      // Force-show close insights for Maximor users on target customer workspaces
      if (showMaximorInternalItems && item.permissionKey === 'close-overview') {
        return true;
      }

      if (!userAccessibleModules || userAccessibleModules.length === 0)
        return false;

      // Check if module name matches item key, key starts with module name (for children), or "All" access
      return userAccessibleModules.some((module) =>
        hasModulePermission(item.permissionKey, module.name)
      );
    };

    // Check if a menu item (including its children) has permission
    const hasPermission = (item: MenuItem): boolean => {
      // FF__DISPLAY_ALL_PAGES is highest priority - show everything
      if (FF__DISPLAY_ALL_PAGES) return true;

      // Demo flag takes precedence over module permissions
      if (item.showdemoitem !== undefined) return item.showdemoitem;

      // If item has a direct path, check its permission
      if ('path' in item && item.path) {
        return hasItemPermission(item);
      }

      // If item has children, check if ANY child has permission
      if ('children' in item && item.children) {
        return item.children.some((child) => hasItemPermission(child));
      }

      return hasItemPermission(item);
    };

    const hasPathPermission = (path: string): boolean => {
      const pathMatches = (itemPath: string, hasPermission: boolean): boolean =>
        hasPermission && (itemPath === path || path.startsWith(`${itemPath}/`));

      // Check top-level items
      for (const item of menuItems) {
        if (
          'path' in item &&
          item.path &&
          pathMatches(item.path, hasItemPermission(item))
        ) {
          return true;
        }
        // Check children - use hasItemPermission which respects showDemoItem flags
        if (item.children) {
          for (const child of item.children) {
            if (
              child.path &&
              pathMatches(child.path, hasItemPermission(child))
            ) {
              return true;
            }
          }
        }
      }
      return false;
    };

    // Filter menu items and also filter children within each item
    const filteredMenuItems = menuItems
      .filter((item) => hasPermission(item))
      .map((item) => {
        if ('children' in item && item.children) {
          return {
            ...item,
            children: item.children.filter((child) => hasItemPermission(child)),
          };
        }
        return item;
      });

    return {
      links: filteredMenuItems,
      hasPathPermission,
      hasModulePermission,
    };
  }, [userAccessibleModules, modulesLoaded, showMaximorInternalItems]);

  return {
    links,
    hasPathPermission,
    hasModulePermission,
  };
}
